import { useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import JernyUserPanel from './JernyUserPanel';
import useSignOutHandler from './useSignOutHandler';
import './JernyUserButton.css';

/**
 * JernyUserButton
 * Custom replacement for Clerk's UserButton.
 * Shows user initials in a circular avatar — click opens JernyUserPanel.
 * Sign out publishes onUserSignOut before delegating to Clerk.
 */
export default function JernyUserButton() {
    const { user } = useUser();
    const { handle: signOut } = useSignOutHandler();
    const [open, setOpen] = useState(false);
    const [anchorPos, setAnchorPos] = useState(null);
    const avatarRef = useRef(null);

    // Derive initials from Clerk user — fallback to '?' if not loaded
    const initials = user
        ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || '?'
        : '?';

    const fullName = user?.fullName ?? '';
    const email = user?.primaryEmailAddress?.emailAddress ?? '';

    /**
     * Opens the panel anchored below and right-aligned to the avatar button.
     */
    const handleAvatarClick = () => {
        if (open) {
            setOpen(false);
            return;
        }

        if (avatarRef.current) {
            const rect = avatarRef.current.getBoundingClientRect();
            setAnchorPos({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right,
            });
        }

        setOpen(true);
    };

    /**
     * Signs the user out.
     * Panel closes first, then delegated to useSignOutHandler.
     */
    const handleSignOut = async () => {
        setOpen(false);
        await signOut();
    };

    /**
     * Closes the panel.
     */
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <div ref={avatarRef} className="jub-avatar" onClick={handleAvatarClick}>
                {initials}
            </div>

            <JernyUserPanel
                open={open}
                anchorPos={anchorPos}
                initials={initials}
                fullName={fullName}
                email={email}
                onSignOut={handleSignOut}
                onClose={handleClose}
            />
        </>
    );
}
